import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import { isUniDataTableFieldShape } from '@/common/utils/omitEmptyUniDataTableFieldsFromReloadPayload'
import { reloadRequestUsesBoundDocument } from '@/common/utils/documentTransferIdentity'

export function normalizeReloadPanelTableField(value: unknown): unknown {
  if (!isUniDataTableFieldShape(value)) return value
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value
  const typed = value as { type?: unknown; value?: Record<string, unknown>; rows?: unknown }
  const valueRows = Array.isArray(typed.value?.rows) ? typed.value.rows : null
  const topRows = Array.isArray(typed.rows) ? typed.rows : null
  const extracted = extractUniDataTableRows(value)
  const rows =
    valueRows && valueRows.length > 0
      ? valueRows
      : topRows && topRows.length > 0
        ? topRows
        : extracted
  if (rows.length === 0) return value
  if (typed.value && typeof typed.value === 'object') {
    return {
      ...typed,
      value: {
        ...typed.value,
        rows,
        // Keep a boolean empty flag. Deleting it lets a stale registry empty:true
        // hide the reloaded rows (EdiTable showHeader=false looks like the table vanished).
        empty: false,
      },
    }
  }
  return { ...typed, rows, empty: false }
}

/**
 * A reload sent as another document must not wipe a table the page already shows.
 * Panel onLoad for the wrong id returns an empty list, and form onLoad then hides it.
 * The bound document may still replace rows with a real empty table.
 */
export function retainNonEmptyLocalTablesOnForeignReload(
  requestFields: Record<string, unknown>,
  pageData: unknown,
  localFields: Record<string, unknown>,
  reloadFields: Record<string, unknown>,
): Record<string, unknown> {
  if (reloadRequestUsesBoundDocument(requestFields, pageData)) return reloadFields
  const next: Record<string, unknown> = { ...reloadFields }
  for (const [key, value] of Object.entries(reloadFields)) {
    if (!isUniDataTableFieldShape(value)) continue
    if (extractUniDataTableRows(value).length > 0) continue
    if (extractUniDataTableRows(localFields[key]).length === 0) continue
    delete next[key]
  }
  return next
}

export function applyReloadPanelFieldsToChangedData(
  changedData: unknown,
  reloadFields: Record<string, unknown>,
): unknown {
  if (!reloadFields || typeof reloadFields !== 'object' || Object.keys(reloadFields).length === 0) {
    return changedData
  }

  const normalizedReloadFields = Object.fromEntries(
    Object.entries(reloadFields).map(([key, value]) => [key, normalizeReloadPanelTableField(value)]),
  )

  if (changedData == null || typeof changedData !== 'object' || Array.isArray(changedData)) {
    return {
      fields: {
        fields: normalizedReloadFields,
      },
    }
  }

  const root = changedData as Record<string, unknown>
  const fieldsWrapper =
    root.fields && typeof root.fields === 'object' && !Array.isArray(root.fields)
      ? { ...(root.fields as Record<string, unknown>) }
      : {}
  const innerFields =
    fieldsWrapper.fields &&
    typeof fieldsWrapper.fields === 'object' &&
    !Array.isArray(fieldsWrapper.fields)
      ? { ...(fieldsWrapper.fields as Record<string, unknown>) }
      : {}

  for (const [key, value] of Object.entries(reloadFields)) {
    innerFields[key] = normalizedReloadFields[key]
  }

  return {
    ...root,
    fields: {
      ...fieldsWrapper,
      fields: innerFields,
    },
  }
}
